"use client";

import { createContext, useContext, useState, useEffect, type ReactNode } from "react";
import { mixpanel, isMixpanelInitialized } from "./mixpanel";
import { AnalyticsEvents } from "./use-analytics";
import type { LocaleCode } from "./api/client";

export interface Language {
  code: LocaleCode;  // BCP 47 locale code
  name: string;      // Display name in English
  nativeName: string;
  flag: string;
  region?: string;   // Optional region for display
}

// Available "I speak" languages (native languages)
export const languages: Language[] = [
  { code: "en-US", name: "English", nativeName: "English", flag: "🇺🇸" },
  { code: "es-ES", name: "Spanish (Spain)", nativeName: "Español", flag: "🇪🇸", region: "Spain" },
  { code: "es-MX", name: "Spanish (Mexico)", nativeName: "Español", flag: "🇲🇽", region: "Mexico" },
  { code: "zh-CN", name: "Chinese (Simplified)", nativeName: "简体中文", flag: "🇨🇳" },
  { code: "ja-JP", name: "Japanese", nativeName: "日本語", flag: "🇯🇵" },
  { code: "he-IL", name: "Hebrew", nativeName: "עברית", flag: "🇮🇱" },
  { code: "ru-RU", name: "Russian", nativeName: "Русский", flag: "🇷🇺" },
  { code: "uk-UA", name: "Ukrainian", nativeName: "Українська", flag: "🇺🇦" },
  { code: "pl-PL", name: "Polish", nativeName: "Polski", flag: "🇵🇱" },
  { code: "fi-FI", name: "Finnish", nativeName: "Suomi", flag: "🇫🇮" },
  { code: "et-EE", name: "Estonian", nativeName: "Eesti", flag: "🇪🇪" },
  { code: "lv-LV", name: "Latvian", nativeName: "Latviešu", flag: "🇱🇻" },
  { code: "el-GR", name: "Greek", nativeName: "Ελληνικά", flag: "🇬🇷" },
  { code: "de-AT", name: "German (Austria)", nativeName: "Deutsch", flag: "🇦🇹", region: "Austria" },
  { code: "de-CH", name: "German (Switzerland)", nativeName: "Deutsch", flag: "🇨🇭", region: "Switzerland" },
];

// Languages available as learning targets (with content)
export const targetLanguages: Language[] = [
  { code: "en-US", name: "English (US)", nativeName: "English", flag: "🇺🇸", region: "US" },
  { code: "en-GB", name: "English (UK)", nativeName: "English", flag: "🇬🇧", region: "UK" },
  { code: "pt-PT", name: "Portuguese (Portugal)", nativeName: "Português Europeu", flag: "🇵🇹", region: "Portugal" },
  { code: "pt-BR", name: "Portuguese (Brazil)", nativeName: "Português Brasileiro", flag: "🇧🇷", region: "Brazil" },
  { code: "es-ES", name: "Spanish (Spain)", nativeName: "Español", flag: "🇪🇸", region: "Spain" },
  { code: "es-MX", name: "Spanish (Mexico)", nativeName: "Español", flag: "🇲🇽", region: "Mexico" },
  { code: "fr-FR", name: "French (France)", nativeName: "Français", flag: "🇫🇷", region: "France" },
  { code: "de-DE", name: "German (Germany)", nativeName: "Deutsch", flag: "🇩🇪", region: "Germany" },
  { code: "de-AT", name: "German (Austria)", nativeName: "Deutsch", flag: "🇦🇹", region: "Austria" },
  { code: "de-CH", name: "German (Switzerland)", nativeName: "Deutsch", flag: "🇨🇭", region: "Switzerland" },
  { code: "it-IT", name: "Italian", nativeName: "Italiano", flag: "🇮🇹" },
  { code: "ja-JP", name: "Japanese", nativeName: "日本語", flag: "🇯🇵" },
  { code: "zh-CN", name: "Chinese (Simplified)", nativeName: "简体中文", flag: "🇨🇳" },
  { code: "ko-KR", name: "Korean", nativeName: "한국어", flag: "🇰🇷" },
  { code: "he-IL", name: "Hebrew", nativeName: "עברית", flag: "🇮🇱" },
  { code: "ru-RU", name: "Russian", nativeName: "Русский", flag: "🇷🇺" },
  { code: "pl-PL", name: "Polish", nativeName: "Polski", flag: "🇵🇱" },
  { code: "uk-UA", name: "Ukrainian", nativeName: "Українська", flag: "🇺🇦" },
  { code: "fi-FI", name: "Finnish", nativeName: "Suomi", flag: "🇫🇮" },
  { code: "et-EE", name: "Estonian", nativeName: "Eesti", flag: "🇪🇪" },
  { code: "lv-LV", name: "Latvian", nativeName: "Latviešu", flag: "🇱🇻" },
  { code: "el-GR", name: "Greek", nativeName: "Ελληνικά", flag: "🇬🇷" },
];

interface LanguageContextType {
  nativeLanguage: Language;
  targetLanguage: Language;
  setNativeLanguage: (lang: Language) => void;
  setTargetLanguage: (lang: Language) => void;
  isLoaded: boolean;
}

const LanguageContext = createContext<LanguageContextType | null>(null);

const STORAGE_KEY = "converso-languages";

// Helper to find language by code (with fallback for old codes without region)
function findLanguage(list: Language[], code: string): Language | undefined {
  // Exact match first
  let lang = list.find((l) => l.code === code);
  if (lang) return lang;

  // Try matching base code (e.g., "pt" matches "pt-PT")
  const baseCode = code.split("-")[0];
  lang = list.find((l) => l.code.startsWith(baseCode + "-"));
  return lang;
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [nativeLanguage, setNativeLanguageState] = useState<Language>(
    languages.find((l) => l.code === "en-US") ?? languages[0]!
  );
  const [targetLanguage, setTargetLanguageState] = useState<Language>(
    targetLanguages.find((l) => l.code === "pt-PT") ?? targetLanguages[0]!
  );
  const [isLoaded, setIsLoaded] = useState(false);

  // Load from localStorage on mount, with URL locale override
  useEffect(() => {
    try {
      // Check for locale query parameter first
      const urlParams = new URLSearchParams(window.location.search);
      const localeParam = urlParams.get("locale");

      if (localeParam) {
        const localeLang = findLanguage(languages, localeParam);
        if (localeLang) {
          setNativeLanguageState(localeLang);
          // Remove locale from URL for cleaner links
          const newUrl = new URL(window.location.href);
          newUrl.searchParams.delete("locale");
          window.history.replaceState({}, "", newUrl.toString());
        }
      }

      // Load stored preferences
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const { native, target } = JSON.parse(stored);
        // Only apply stored native if no locale param was used
        if (!localeParam) {
          const nativeLang = findLanguage(languages, native);
          if (nativeLang) setNativeLanguageState(nativeLang);
        }
        const targetLang = findLanguage(targetLanguages, target);
        if (targetLang) setTargetLanguageState(targetLang);
      }
    } catch {
      // Ignore storage errors
    }
    setIsLoaded(true);
  }, []);

  // Save to localStorage on change
  useEffect(() => {
    if (isLoaded) {
      try {
        localStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({ native: nativeLanguage.code, target: targetLanguage.code })
        );
      } catch {
        // Ignore storage errors
      }
    }
  }, [nativeLanguage, targetLanguage, isLoaded]);

  const setNativeLanguage = (lang: Language) => {
    const prevCode = nativeLanguage.code;
    setNativeLanguageState(lang);

    // Track language change
    if (isMixpanelInitialized() && prevCode !== lang.code) {
      mixpanel.track(AnalyticsEvents.LANGUAGE_CHANGED, {
        from: prevCode,
        to: lang.code,
        type: "native",
      });
    }
  };

  const setTargetLanguage = (lang: Language) => {
    const prevCode = targetLanguage.code;
    setTargetLanguageState(lang);

    // Track language change
    if (isMixpanelInitialized() && prevCode !== lang.code) {
      mixpanel.track(AnalyticsEvents.LANGUAGE_CHANGED, {
        from: prevCode,
        to: lang.code,
        type: "target",
      });
    }
  };

  return (
    <LanguageContext.Provider
      value={{ nativeLanguage, targetLanguage, setNativeLanguage, setTargetLanguage, isLoaded }}
    >
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
}

